import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  FormControl,
  TextField,
  MenuItem,
  Box,
  InputAdornment,
  IconButton,
  Paper,
  Typography,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { FaAngleRight } from "react-icons/fa";
import { FaAnglesRight } from "react-icons/fa6";
import { GoDotFill } from "react-icons/go";

const NestedCategorySearch = ({ categories, value, onChange, error }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const categoriesMap = useMemo(() => {
    const map = new Map();
    const rootCategories = [];

    categories.forEach((category) => {
      map.set(category.id, { ...category, children: [] });
    });

    categories.forEach((category) => {
      if (category.parentId) {
        map.get(category.parentId)?.children.push(map.get(category.id));
      } else {
        rootCategories.push(map.get(category.id));
      }
    });

    return { map, rootCategories };
  }, [categories]);

  // Arama terimine göre kategorileri filtreleme
  const filteredCategories = useMemo(() => {
    const filterRecursive = (categories) =>
      categories.reduce((acc, category) => {
        const matches = category.name.toLowerCase().includes(searchTerm.toLowerCase());
        const filteredChildren = filterRecursive(category.children || []);

        if (matches || filteredChildren.length > 0) {
          acc.push({ ...category, children: filteredChildren });
        }
        return acc;
      }, []);
    return filterRecursive(categoriesMap.rootCategories);
  }, [categoriesMap.rootCategories, searchTerm]);


  const renderCategoryMenuItems = (categories, level = 0) =>
    categories.map((category) => (
      <React.Fragment key={category.id}>
        <ListItem
          button
          onClick={() => handleCategorySelect(category.id)}
          sx={{
            pl: `${level * 2 + 2}rem`,
            bgcolor: level % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.06)',
            borderRadius: 1,
            mb: 1,
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {category.children?.length > 0 ? (
              <FaAnglesRight />
            ) : (
              <GoDotFill />

            )}
          </ListItemIcon>
          <ListItemText
            primary={category.name}
            primaryTypographyProps={{
              fontWeight: level === 0 ? 'bold' : 'normal',
              fontSize: level === 0 ? '1rem' : '0.9rem',
            }}
          />
        </ListItem>
        {category.children?.length > 0 &&
          renderCategoryMenuItems(category.children, level + 1)}
      </React.Fragment>
    ));

  const handleCategorySelect = (categoryId) => {
    onChange(categoryId);
    setOpen(false);
  };

  return (
    <FormControl fullWidth size="small" error={!!error} ref={dropdownRef}>
      <TextField 
        variant="outlined"
        size="small"
        placeholder="Kategori Seçiniz"
        value={categoriesMap.map.get(value)?.name || ''}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setOpen(!open)}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ 
          cursor: 'pointer',
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        onClick={() => setOpen(true)}
      />
      {open && (
        <Paper
          sx={{
            position: 'absolute',
            width: '100%',
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 10,
            mt: 1,
            p: 1,
            border: '1px solid #ddd',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <TextField 
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Kategori ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
            {renderCategoryMenuItems(filteredCategories)}
          </Box>
        </Paper>
      )}
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </FormControl>
  );
};

export default NestedCategorySearch;
